import {isEqual} from 'lodash';
import {getString, getObject} from '@packages/utilities/json';
import {
  type ExpressionPath,
  type ElementF,
  matchExpression,
  Plugins,
  Component,
  Expressions,
  Variable,
} from '@packages/systems/wfdl';
import {
  identifier,
  namesEqual,
  qualify,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {weakMemo} from '@packages/utilities/memo';
import {
  concat,
  emptyArray,
  anyPass,
  allPass,
  complement,
} from '@packages/utilities/fp/utils';

import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';

import {RESERVED_USER_PREFIX} from '@packages/systems/users/constants';

import type {SiteUsersState} from '@packages/systems/users/flux';

import {USYS_FIELD_PATH} from '@packages/systems/users/usysWfdl';

/**
 * @public
 * @file
 * Element type checkers can be a code-smell that we are adding special support for a specific element rather
 * than leveraging or creating new platform APIs that would empower our ecosystem. Before creating a new element type checker
 * or leveraging an existing one consider, should this functionality be generalized by our platform?
 */

/**
 * Element type qualified constants
 */
const htmlEmbedType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Embed'),
  Component.name('HtmlEmbed')
);
const customCodeType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Ssr'),
  Component.name('CustomCode')
);
const commerceCartListType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartList')
);
const commerceCheckoutOrderItemOptionListType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderItemOptionList')
  );
const commerceCheckoutOrderItemsListType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderItemsList')
  );
const commerceShippingMethodsListType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceShippingMethodsList')
  );
const commerceCheckoutOrderSummaryExtraItemsListType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderSummaryExtraItemsList')
  );
const commerceAddToCartOptionListType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceAddToCartOptionList')
  );
const commerceCartOptionListType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartOptionList')
);
const commerceDownloadsListType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceDownloadsList')
);
const commerceDownloadsWrapperType: Qualified<Component.ComponentName> =
  qualify(Plugins.name('Commerce'), Component.name('CommerceDownloadsWrapper'));
const commerceAddToCartOptionType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceAddToCartOption')
);
const commerceAddToCartButtonType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceAddToCartButton')
);
const commerceAddToCartOptionPillType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceAddToCartOptionPill')
  );

const commerceCartOptionListItemType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCartOptionListItem')
  );
const commerceCheckoutOrderItemType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderItem')
  );
const commerceCheckoutShippingMethodItemType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingMethodItem')
  );
const commerceAddToCartOptionLabelType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceAddToCartOptionLabel')
  );
const commerceCheckoutAdditionalCheckboxType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutAdditionalCheckbox')
  );
const commerceCheckoutAdditionalTextInputType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutAdditionalTextInput')
  );
const commerceCheckoutBillingAddressToggleCheckboxType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingAddressToggleCheckbox')
  );
const commerceCheckoutBillingCityType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingCity')
  );
const commerceCheckoutBillingFullNameType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingFullName')
  );
const commerceCheckoutBillingStateProvinceType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingStateProvince')
  );
const commerceCheckoutBillingStreetAddressType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingStreetAddress')
  );
const commerceCheckoutBillingStreetAddressOptionalType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingStreetAddressOptional')
  );
const commerceCheckoutBillingZipPostalCodeType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingZipPostalCode')
  );
const commerceCheckoutEmailInputType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutEmailInput')
  );
const commerceCheckoutShippingCityType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingCity')
  );
const commerceCheckoutShippingFullNameType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingFullName')
  );
const commerceCheckoutShippingStateProvinceType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingStateProvince')
  );
const commerceCheckoutShippingStreetAddressType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingStreetAddress')
  );
const commerceCheckoutShippingStreetAddressOptionalType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingStreetAddressOptional')
  );
const commerceCheckoutShippingZipPostalCodeType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingZipPostalCode')
  );
const commerceCheckoutShippingCountrySelectorType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutShippingCountrySelector')
  );
const commerceCheckoutBillingCountrySelectorType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingCountrySelector')
  );
const commerceCartQuantityType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartQuantity')
);
const commerceAddToCartQuantityInputType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceAddToCartQuantityInput')
  );
const commerceCheckoutDiscountsInputType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutDiscountsInput')
  );
const commerceCheckoutCardNumberType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutCardNumber')
  );
const commerceCheckoutCardExpirationDateType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutCardExpirationDate')
  );
const commerceCheckoutCardSecurityCodeType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutCardSecurityCode')
  );
const commerceCheckoutAdditionalTextAreaType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutAdditionalTextArea')
  );
const commerceCheckoutAdditionalCheckboxWrapperType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutAdditionalCheckboxWrapper')
  );

const userSignUpForm = qualify(
  Plugins.name('Users'),
  Component.name('UserSignUpForm')
);

const userAccountForm = qualify(
  Plugins.name('Users'),
  Component.name('UserAccountForm')
);

const userFormTermsOfServiceType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserSignUpTermsOfServiceCheckboxInput')
);

const userFormCheckboxInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormCheckboxInput')
);

const userFormTextInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormTextInput')
);

const userFormNumberInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormNumberInput')
);

const userFormSelectInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormSelect')
);

const userFormEmailInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormEmailInput')
);
const userFormNameInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormNameInput')
);
const userFormPasswordInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormPasswordInput')
);

const userFormFileInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormFileUploadInput')
);
const commerceCheckoutOrderSummaryExtraItemsListItemType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderSummaryExtraItemsListItem')
  );
const commerceCheckoutOrderItemOptionListItemType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderItemOptionListItem')
  );
const commerceDownloadsItemType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceDownloadsItem')
);
const commerceAddToCartOptionSelectType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceAddToCartOptionSelect')
  );
const commerceAddToCartFormType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceAddToCartForm')
);
const commerceAddToCartWrapperType: Qualified<Component.ComponentName> =
  qualify(Plugins.name('Commerce'), Component.name('CommerceAddToCartWrapper'));
const commerceOrderConfirmationContainerType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceOrderConfirmationContainer')
  );
const commerceCheckoutFormContainerType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutFormContainer')
  );
const commerceCartWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartWrapper')
);
const commerceCartProductNameType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartProductName')
);
const commerceCartProductPriceType: Qualified<Component.ComponentName> =
  qualify(Plugins.name('Commerce'), Component.name('CommerceCartProductPrice'));
const commerceCartOrderValueType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartOrderValue')
);
const commerceCartOptionListItemLabelType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCartOptionListItemLabel')
  );
const commerceCartOptionListItemValueType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCartOptionListItemValue')
  );
const commerceCheckoutOrderItemOptionListItemLabelType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderItemOptionListItemLabel')
  );
const commerceCheckoutOrderItemOptionListItemValueType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutOrderItemOptionListItemValue')
  );
const commerceCheckoutSummaryTotalType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutSummaryTotal')
  );
const commerceCheckoutSummaryTextSpacingOnDivType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutSummaryTextSpacingOnDiv')
  );
const commerceBoldTextBlockType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceBoldTextBlock')
);
const commerceCartOpenLinkCountType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCartOpenLinkCount')
  );
const commerceCartItemImageType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartItemImage')
);

const lineBreakType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('LineBreak')
);
const iframeType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Iframe')
);
const richTextType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('RichText')
);
const blockType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Block')
);
const figureType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Figure')
);
const figCaptionType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Figcaption')
);
const imageType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Image')
);
const linkType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Link')
);

const dynamoWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dynamo'),
  Component.name('DynamoWrapper')
);
const dynamoListType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dynamo'),
  Component.name('DynamoList')
);
const dynamoItemType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dynamo'),
  Component.name('DynamoItem')
);
const dynamoEmptyType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dynamo'),
  Component.name('DynamoEmpty')
);
const localesWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Localization'),
  Component.name('LocalesWrapper')
);
const localesListType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Localization'),
  Component.name('LocalesList')
);
const localesItemType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Localization'),
  Component.name('LocalesItem')
);
const paginationType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Pagination'),
  Component.name('Pagination')
);
const searchResultItemType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Search'),
  Component.name('SearchResultItem')
);
const searchInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Search'),
  Component.name('SearchInput')
);
const searchButtonType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Search'),
  Component.name('SearchButton')
);
const formSuccessMessageType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormSuccessMessage')
);
const formErrorMessageType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormErrorMessage')
);
const formWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormWrapper')
);

const lightboxWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Lightbox'),
  Component.name('LightboxWrapper')
);
const navbarBrandType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Navbar'),
  Component.name('NavbarBrand')
);
const navbarContainerType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Navbar'),
  Component.name('NavbarContainer')
);

const formFileUploadWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormFileUploadWrapper')
);

const formFileUploadLabelType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormFileUploadLabel')
);
const formFileUploadInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormFileUploadInput')
);
const formFileUploadErrorMsgType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormFileUploadErrorMsg')
);
const paginationNextType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Pagination'),
  Component.name('PaginationNext')
);
const paginationPreviousType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Pagination'),
  Component.name('PaginationPrevious')
);
const paginationCountType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Pagination'),
  Component.name('PaginationCount')
);
const listItemType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('ListItem')
);
const listType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('List')
);
const blockquoteType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Blockquote')
);
const formFormType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormForm')
);
const formButtonType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormButton')
);
const formTextInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormTextInput')
);
const tabsPaneType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Tabs'),
  Component.name('TabsPane')
);
const tabsLinkType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Tabs'),
  Component.name('TabsLink')
);
const tabsContentType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Tabs'),
  Component.name('TabsContent')
);
const tabsMenuType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Tabs'),
  Component.name('TabsMenu')
);
const tabsWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Tabs'),
  Component.name('TabsWrapper')
);
const navbarWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Navbar'),
  Component.name('NavbarWrapper')
);
const dropdownWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dropdown'),
  Component.name('DropdownWrapper')
);
const dropdownListType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dropdown'),
  Component.name('DropdownList')
);
const dropdownToggleType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dropdown'),
  Component.name('DropdownToggle')
);

const componentOnlyDropTargetType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('PageBuilding'),
  Component.name('DropTarget')
);

const formPlaceholderType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('PageBuilding'),
  Component.name('FormPlaceholder')
);

const slotType = qualify(Plugins.name('Slots'), Component.name('Slot'));
const sliderWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Slider'),
  Component.name('SliderWrapper')
);
const sliderMaskType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Slider'),
  Component.name('SliderMask')
);
const sliderArrowType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Slider'),
  Component.name('SliderArrow')
);
const sliderNavType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Slider'),
  Component.name('SliderNav')
);
const twitterType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Widget'),
  Component.name('Twitter')
);
const facebookType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Widget'),
  Component.name('Facebook')
);
const googleType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Widget'),
  Component.name('Google')
);
const mapType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Widget'),
  Component.name('Map')
);
const mapWidgetType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Widget'),
  Component.name('MapWidget')
);
const iconType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Icon'),
  Component.name('Icon')
);
const formFileUploadUploadingIconType: Qualified<Component.ComponentName> =
  qualify(Plugins.name('Form'), Component.name('FormFileUploadUploadingIcon'));
const videoType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Embed'),
  Component.name('Video')
);
const youTubeVideoType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Embed'),
  Component.name('YouTubeVideo')
);
const sliderSlideType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Slider'),
  Component.name('SliderSlide')
);

const formCheckboxWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormCheckboxWrapper')
);

const formSelectType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormSelect')
);
const rowType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('Row')
);
const columnType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('Column')
);
const gridType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('Grid')
);
const containerType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('Container')
);
const layoutType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('Layout')
);
const cellType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('Cell')
);
const vFlexType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('VFlex')
);
const hFlexType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('HFlex')
);
const blockContainerType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Layout'),
  Component.name('BlockContainer')
);

const formTextareaType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormTextarea')
);

const navbarLinkType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Navbar'),
  Component.name('NavbarLink')
);

const navbarButtonType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Navbar'),
  Component.name('NavbarButton')
);

const navbarMenuType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Navbar'),
  Component.name('NavbarMenu')
);

const formRadioWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormRadioWrapper')
);

const formRadioInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormRadioInput')
);

const formCheckboxInputType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormCheckboxInput')
);

const searchFormType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Search'),
  Component.name('SearchForm')
);

const dropdownLinkType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Dropdown'),
  Component.name('DropdownLink')
);

const formBlockLabelType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormBlockLabel')
);

const formReCaptchaType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormReCaptcha')
);
const formInlineLabelType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Form'),
  Component.name('FormInlineLabel')
);

const searchResultWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Search'),
  Component.name('SearchResultWrapper')
);

const searchResultListType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Search'),
  Component.name('SearchResultList')
);

const searchResultEmptyType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Search'),
  Component.name('SearchResultEmpty')
);

const htmlType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Ssr'),
  Component.name('Document')
);

const dangerousDomType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Builtin'),
  Component.name('DangerousDOM')
);

const commercePaypalCheckoutFormContainerType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommercePaypalCheckoutFormContainer')
  );

const commercePayPalCheckoutButtonType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommercePayPalCheckoutButton')
  );

const strongType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Strong')
);

const headingType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Heading')
);

const emphasizedType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Emphasized')
);

const subscriptType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Subscript')
);

const superscriptType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Superscript')
);

const inlineCodeType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('InlineCode')
);

const codeBlockType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('CodeBlock')
);

const userFormBlockLabelType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserFormBlockLabel')
);

const userLogOutLogInType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserLogOutLogIn')
);

const paragraphType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Paragraph')
);

const spanType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Basic'),
  Component.name('Span')
);

const lottieType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Animation'),
  Component.name('Animation') // Lottie
);

const splineType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Animation'),
  Component.name('Spline')
);

const riveType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Animation'),
  Component.name('Rive')
);

const frameType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Frame'),
  Component.name('Frame')
);

// The UserAccountWrapper factory is accidentally using `User` as the plugin
// name even though `Users` would be the correct one. Element reification
// patches wrong plugin name in the element type, so we need to check for both
// type names when detecting UserAccountWrappers. Once the element factory
// no longer uses `User` plugin name, we can stop checking for that.
const legacyUserAccountWrapperType: Qualified<Component.ComponentName> =
  qualify(Plugins.name('User'), Component.name('UserAccountWrapper'));
const userAccountWrapperType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Users'),
  Component.name('UserAccountWrapper')
);

// User Subscriptions
const userAccountSubscriptionListWrapperType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Users'),
    Component.name('UserAccountSubscriptionListWrapper')
  );

const userAccountSubscriptionListType: Qualified<Component.ComponentName> =
  qualify(Plugins.name('Users'), Component.name('UserAccountSubscriptionList'));

const userAccountSubscriptionListItemType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Users'),
    Component.name('UserAccountSubscriptionListItem')
  );

const bgVideoWrapper = qualify(
  Plugins.name('BackgroundVideo'),
  Component.name('BackgroundVideoWrapper')
);

const bgVideoPlayPauseButton = qualify(
  Plugins.name('BackgroundVideo'),
  Component.name('BackgroundVideoPlayPauseButton')
);

const domType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Builtin'),
  Component.name('DOM')
);

const bodyType = qualify(Plugins.name('Body'), Component.name('Body'));

export const stringType = qualify(
  Plugins.name('Basic'),
  Component.name('String')
);

export const elementTypeEquals = weakMemo(
  (targetType: Qualified<Component.ComponentName>) => (el: ExpressionElement) =>
    namesEqual(el.type, targetType)
);

/**
 * Checks for an element that will eventually be a
 * [Node.TEXT_NODE - 3](https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType)
 *
 * Typographic Elements constain String elements.
 */
export function isStringElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, stringType);
}

export function isBody(element: ExpressionElement): boolean {
  return namesEqual(element.type, bodyType);
}

export function isDOM(element: ExpressionElement): boolean {
  return namesEqual(element.type, domType);
}

export function isHeading(element: ExpressionElement): boolean {
  return namesEqual(element.type, headingType);
}

export function isBlockquote(element: ExpressionElement): boolean {
  return namesEqual(element.type, blockquoteType);
}

export function isStrong(element: ExpressionElement): boolean {
  return namesEqual(element.type, strongType);
}

export function isEmphasized(element: ExpressionElement): boolean {
  return namesEqual(element.type, emphasizedType);
}
export function isSubscript(element: ExpressionElement): boolean {
  return namesEqual(element.type, subscriptType);
}

export function isSuperscript(element: ExpressionElement): boolean {
  return namesEqual(element.type, superscriptType);
}

export function isInlineCode(element: ExpressionElement): boolean {
  return namesEqual(element.type, inlineCodeType);
}

export function isCodeBlock(element: ExpressionElement): boolean {
  return namesEqual(element.type, codeBlockType);
}

export function isDivBlock(element: ExpressionElement): boolean {
  return namesEqual(element.type, blockType);
}

export function isContainer(element: ExpressionElement): boolean {
  return namesEqual(element.type, containerType);
}

export function isBlockContainer(element: ExpressionElement): boolean {
  return namesEqual(element.type, blockContainerType);
}

export function isRow(element: ExpressionElement): boolean {
  return namesEqual(element.type, rowType);
}

export function isColumn(element: ExpressionElement): boolean {
  return namesEqual(element.type, columnType);
}

export function isLayout(element: ExpressionElement): boolean {
  return namesEqual(element.type, layoutType);
}

export function isLayoutElementPreset(
  elementPreset: Plugins.QualifiedElementPreset
): boolean {
  return isEqual(elementPreset, layoutType);
}

export function isCell(element: ExpressionElement): boolean {
  return namesEqual(element.type, cellType);
}

export function isVFlex(element: ExpressionElement): boolean {
  return namesEqual(element.type, vFlexType);
}

export function isHFlex(element: ExpressionElement): boolean {
  return namesEqual(element.type, hFlexType);
}

const GRID_TYPE_PATH = [
  {in: 'Record', at: 'grid'},
  {in: 'Record', at: 'type'},
] satisfies ExpressionPath;

export function isGrid(element: ExpressionElement): boolean {
  return namesEqual(element.type, gridType);
}

export function isGridContainer(element: ExpressionElement): boolean {
  return (
    isContainer(element) &&
    Expressions.getElementDataAt(element, GRID_TYPE_PATH, 'Text', '') ===
      'container'
  );
}

export function isGridOrBlockContainer(element: ExpressionElement): boolean {
  return isGridContainer(element) || isBlockContainer(element);
}

/*
 * Indicates whether a node is a grid row.
 */
export function isGridRow(element: ExpressionElement): boolean {
  return (
    isRow(element) &&
    Expressions.getElementDataAt(element, GRID_TYPE_PATH, 'Text', '') === 'row'
  );
}

/**
 * Indicates whether an element is an individual grid column inside of a grid row.
 */
export function isGridColumn(element: ExpressionElement): boolean {
  return (
    isColumn(element) &&
    Expressions.getElementDataAt(element, GRID_TYPE_PATH, 'Text', '') === 'col'
  );
}

export function isCommerceCartOptionListItemLabel(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartOptionListItemLabelType);
}

export function isCommerceCheckoutOrderItemOptionListItemLabel(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutOrderItemOptionListItemLabelType
  );
}

export function isCommerceCheckoutOrderItem(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutOrderItemType);
}

export function isCommerceShippingMethodItem(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutShippingMethodItemType);
}

export function isCommerceAddToCartOptionLabel(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceAddToCartOptionLabelType);
}

export function isCommerceCheckoutShippingStreetAddress(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutShippingStreetAddressType);
}

export function isCommerceCheckoutAdditionalCheckbox(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutAdditionalCheckboxType);
}

export function isCommerceCheckoutAdditionalTextInput(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutAdditionalTextInputType);
}
export function isCommerceCheckoutBillingAddressToggleCheckbox(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutBillingAddressToggleCheckboxType
  );
}
export function isCommerceCheckoutBillingCity(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutBillingCityType);
}
export function isCommerceCheckoutBillingFullName(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutBillingFullNameType);
}
export function isCommerceCheckoutBillingStateProvince(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutBillingStateProvinceType);
}
export function isCommerceCheckoutBillingStreetAddress(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutBillingStreetAddressType);
}
export function isCommerceCheckoutBillingStreetAddressOptional(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutBillingStreetAddressOptionalType
  );
}
export function isCommerceCheckoutBillingZipPostalCode(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutBillingZipPostalCodeType);
}
export function isCommerceCheckoutEmailInput(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutEmailInputType);
}
export function isCommerceCheckoutShippingCity(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutShippingCityType);
}
export function isCommerceCheckoutShippingFullName(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutShippingFullNameType);
}
export function isCommerceCheckoutShippingStateProvince(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutShippingStateProvinceType);
}
export function isCommerceCheckoutShippingStreetAddressOptional(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutShippingStreetAddressOptionalType
  );
}
export function isCommerceCheckoutShippingZipPostalCode(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutShippingZipPostalCodeType);
}
export function isCommerceAddToCartOptionSelect(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceAddToCartOptionSelectType);
}
export function isCommerceCheckoutBillingCountrySelector(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutBillingCountrySelectorType);
}
export function isCommerceCheckoutShippingCountrySelector(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutShippingCountrySelectorType);
}
export function isCommerceCartQuantity(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartQuantityType);
}
export function isCommerceAddToCartQuantityInput(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceAddToCartQuantityInputType);
}
export function isCommerceCheckoutDiscountsInput(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutDiscountsInputType);
}
export function isCommerceCheckoutCardNumber(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutCardNumberType);
}
export function isCommerceCheckoutStripeElement(
  element: ExpressionElement
): boolean {
  return (
    namesEqual(element.type, commerceCheckoutCardExpirationDateType) ||
    namesEqual(element.type, commerceCheckoutCardNumberType) ||
    namesEqual(element.type, commerceCheckoutCardSecurityCodeType)
  );
}

export const isCommerceCartList = (element: ExpressionElement): boolean => {
  return namesEqual(element.type, commerceCartListType);
};

export const isCommerceCheckoutOrderItemsList = (
  element: ExpressionElement
): boolean => {
  return namesEqual(element.type, commerceCheckoutOrderItemsListType);
};

export const isCommerceOptionListOrItem = (
  element: ExpressionElement
): boolean => {
  return (
    isCommerceCartOptionListItem(element) ||
    isCommerceCheckoutOrderItemOptionListItem(element) ||
    isCommerceAddToCartOption(element)
  );
};

export function isCommerceCheckoutAdditionalTextArea(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutAdditionalTextAreaType);
}
export function isCommerceCheckoutAdditionalCheckboxWrapper(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutAdditionalCheckboxWrapperType
  );
}

export function isGenericUserInput(element: ExpressionElement): boolean {
  return (
    isUserFormTextInput(element) ||
    isUserFormCheckboxInput(element) ||
    isUserFormSelectInput(element) ||
    isUserFormFileInput(element)
  );
}

export function isUserSignUpForm(element: ExpressionElement): boolean {
  return namesEqual(element.type, userSignUpForm);
}

export function isUserAccountForm(element: ExpressionElement): boolean {
  return namesEqual(element.type, userAccountForm);
}

export const isDeletedUserFormCustomField = (
  element: ExpressionElement,
  SiteUsersStore: SiteUsersState
): boolean => {
  const userFields = SiteUsersStore?.userFields?.fields ?? [];
  const fieldId = Expressions.getElementDataAt(
    element,
    USYS_FIELD_PATH,
    'Text',
    ''
  );

  if (
    !fieldId ||
    !isUserFormTextInput(element) ||
    fieldId.includes(RESERVED_USER_PREFIX)
  )
    return false;

  return !userFields.some((field) => field.id === fieldId);
};

export function isUserFormInput(element: ExpressionElement): boolean {
  return (
    isUserFormTermsOfService(element) ||
    isUserFormCheckboxInput(element) ||
    isUserFormTextInput(element) ||
    isUserFormEmailInput(element) ||
    isUserFormNameInput(element) ||
    isUserFormPasswordInput(element) ||
    isUserFormSelectInput(element) ||
    isUserFormFileInput(element)
  );
}

export function isUserFormTermsOfService(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormTermsOfServiceType);
}

export function isUserFormCheckboxInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormCheckboxInputType);
}

export function isUserFormTextInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormTextInputType);
}

export function isUserFormNumberInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormNumberInputType);
}

export function isUserFormSelectInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormSelectInputType);
}

export function isUserFormEmailInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormEmailInputType);
}
export function isUserFormNameInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormNameInputType);
}
export function isUserFormPasswordInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormPasswordInputType);
}

export function isUserFormFileInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormFileInputType);
}

export function isCommerceCheckoutOrderSummaryExtraItemsListItem(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutOrderSummaryExtraItemsListItemType
  );
}

export function isCommerceCartOptionListItem(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartOptionListItemType);
}

export function isCommerceCheckoutOrderItemOptionListItem(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutOrderItemOptionListItemType);
}

export function isCommerceAddToCartOption(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceAddToCartOptionType);
}

export function isCommerceAddToCartOptionPill(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceAddToCartOptionPillType);
}

export function isUserFormBlockLabel(element: ExpressionElement): boolean {
  return namesEqual(element.type, userFormBlockLabelType);
}

export function isUserLogOutLogInType(element: ExpressionElement): boolean {
  return namesEqual(element.type, userLogOutLogInType);
}

export function isParagraph(element: ExpressionElement): boolean {
  return namesEqual(element.type, paragraphType);
}

export function isSpan(element: ExpressionElement): boolean {
  return namesEqual(element.type, spanType);
}

function isCommerceCartOptionListItemValue(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartOptionListItemValueType);
}

function isCommerceCheckoutOrderItemOptionListItemValue(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutOrderItemOptionListItemValueType
  );
}

const commerceCheckoutDiscountsButtonType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutDiscountsButton')
  );

export function isCommerceCheckoutDiscountsButton(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutDiscountsButtonType);
}

const commerceCheckoutBillingAddressToggleLabelType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutBillingAddressToggleLabel')
  );

export function isCommerceCheckoutBillingAddressToggleLabel(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceCheckoutBillingAddressToggleLabelType
  );
}

const commerceCheckoutSummaryLabelType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutSummaryLabel')
  );

export function isCommerceCheckoutSummaryLabel(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutSummaryLabelType);
}

export function isCommerceCheckoutSummaryTextSpacingOnDiv(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutSummaryTextSpacingOnDivType);
}

const commerceLabelType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceLabel')
);

export function isCommerceLabel(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceLabelType);
}

const commerceCheckoutLabelType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCheckoutLabel')
);

export function isCommerceCheckoutLabel(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCheckoutLabelType);
}

const commerceBuyNowButtonType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceBuyNowButton')
);

export function isCommerceBuyNowButton(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceBuyNowButtonType);
}

export function isCommerceAddToCartButton(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceAddToCartButtonType);
}

const commerceCartCheckoutButtonType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCartCheckoutButton')
  );

export function isCommerceCartCheckoutButton(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartCheckoutButtonType);
}

const commerceCartCloseLinkType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartCloseLink')
);

function isCommerceCartCloseLink(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartCloseLinkType);
}

const commerceCartOpenLinkType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartOpenLink')
);

function isCommerceCartOpenLink(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartOpenLinkType);
}

const commerceCartQuickCheckoutButtonType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCartQuickCheckoutButton')
  );

export function isCommerceCartQuickCheckoutButton(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartQuickCheckoutButtonType);
}

const commerceCartRemoveLinkType: Qualified<Component.ComponentName> = qualify(
  Plugins.name('Commerce'),
  Component.name('CommerceCartRemoveLink')
);

function isCommerceCartRemoveLink(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartRemoveLinkType);
}

const commerceCheckoutPlaceOrderButtonType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutPlaceOrderButton')
  );

export function isCommerceCheckoutPlaceOrderButton(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutPlaceOrderButtonType);
}

const commerceCartApplePayButtonType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCartApplePayButton')
  );

export function isCommerceCartApplePayButton(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartApplePayButtonType);
}

const commerceCheckoutDiscountsLabelType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceCheckoutDiscountsLabel')
  );

export function isCommerceCheckoutDiscountsLabel(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutDiscountsLabelType);
}
/**
 * Checks to see if an Element is typographic. i.g: When double-clicking a
 * Block, the property `text` is set on the Block element and a `String` element
 * is added to it's children.
 * For the Designer, this indicates that the contents of the element
 * are purely textual and can be edited on canvas in the content editor
 */
export function isTypographicElement(element: ExpressionElement): boolean {
  return Expressions.getElementDataAt(
    element,
    [{in: 'Record', at: 'text'}],
    'Boolean',
    false
  );
}

export function isTypographicBlock(element: ExpressionElement): boolean {
  return isTypographicElement(element) && isAllowedTypographic(element);
}

/**
 * Checks if an element is only containing plain text content
 */
export function isPlainTextElement(element: ExpressionElement): boolean {
  return (
    isStringElement(element) ||
    isLineBreakElement(element) ||
    isStrong(element) ||
    isEmphasized(element) ||
    isSuperscript(element) ||
    isSubscript(element) ||
    isInlineCode(element)
  );
}

function isTypographicDOM(element: ExpressionElement): boolean {
  return (
    isDOM(element) &&
    Expressions.getElementDataAt(
      element,
      [{in: 'Record', at: 'text'}],
      'Boolean',
      false
    ) &&
    Expressions.getElementDataAt(
      element,
      [{in: 'Record', at: 'tag'}],
      'Text',
      Expressions.getElementDataAt(
        element,
        [{in: 'Record', at: 'tag'}],
        'Enum',
        undefined
      )
    ) === 'span'
  );
}

/**
 * Indicates whether a element is typographic (contains only text or content nodes).
 */
export function isTypographic(element: ExpressionElement): boolean {
  // Navbar brand links are treated as block elements that can't be typographic
  if (isNavbarBrand(element)) {
    return false;
  }

  // Form File Upload labels are treated as block elements that can't be typographic
  if (isFormFileUploadLabel(element)) {
    return false;
  }

  // Some tags are always typographic
  if (
    isFormLabel(element) ||
    isHeading(element) ||
    isBlockquote(element) ||
    isStrong(element) ||
    isEmphasized(element) ||
    isSuperscript(element) ||
    isSubscript(element) ||
    isInlineCode(element) ||
    isCommerceCartOptionListItemLabel(element) ||
    isCommerceCheckoutOrderItemOptionListItemLabel(element) ||
    isUserFormBlockLabel(element) ||
    isParagraph(element) ||
    isSpan(element) ||
    isCommerceCartOptionListItemValue(element) ||
    isCommerceCheckoutOrderItemOptionListItemValue(element) ||
    isCommerceCheckoutDiscountsButton(element) ||
    isCommerceCheckoutBillingAddressToggleLabel(element) ||
    isCommerceCheckoutSummaryLabel(element) ||
    isCommerceLabel(element) ||
    isCommerceCheckoutLabel(element) ||
    isCommerceCartApplePayButton(element) ||
    isCommerceCheckoutDiscountsLabel(element) ||
    isTypographicDOM(element)
  ) {
    return true;
  }

  // Anchor tags can sometimes be block elements (e.g. for Button Block elements)
  if (isLink(element)) {
    const block =
      Expressions.getElementDataAt(
        element,
        [{in: 'Record', at: 'block'}],
        'Boolean',
        false
      ) ||
      Expressions.getElementDataAt(
        element,
        [{in: 'Record', at: 'block'}],
        'Text',
        ''
      ) === 'inline';
    return (
      !block && !isPaginationNext(element) && !isPaginationPrevious(element)
    );
  }

  // Div/LI blocks can sometimes be typographic (e.g. for Text Block elements)
  if (isTypographicBlock(element)) {
    return true;
  }

  return false;
}

export function isLineBreakElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, lineBreakType);
}

export function isIframeElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, iframeType);
}

/**
 * Detects whether an element is the root html element.
 */
export function isHtml(element: ExpressionElement): boolean {
  return namesEqual(element.type, htmlType);
}

export function isLinkElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, linkType);
}

const overlayTargetContext = Variable.unqualified(identifier('context'));

const getOverlayTargetCallMode = (
  expression: Expression
): 'popover' | 'modal' | null => {
  const {fun, args} = Expressions.deconstructCall(expression);
  const select = Expressions.getSelect(fun);
  const mode =
    select?.prop === 'popoverTarget'
      ? 'popover'
      : select?.prop === 'modalTarget'
        ? 'modal'
        : null;
  if (!args.length || !select || !mode) return null;

  const variable = Expressions.getVariable(select.from);
  return variable && Variable.equals(overlayTargetContext)(variable)
    ? mode
    : null;
};

const linkModeMatcher = matchExpression({
  Record: (fields) => {
    const linkVal = fields.link
      ? Expressions.getLiteral(fields.link)?.value
      : null;
    if (linkVal) {
      const linkObj = getObject(linkVal);
      if (!linkObj) return null;
      const {mode} = linkObj;
      if (!mode) return null;
      return getString(mode) || null;
    }

    return fields.link ? getOverlayTargetCallMode(fields.link) : null;
  },
  _: () => null,
});

export function getLinkElementMode(element: ExpressionElement): string | null {
  return linkModeMatcher(Expressions.getElementData(element));
}

export function isButton(element: ExpressionElement): boolean {
  return Expressions.getElementDataAt(
    element,
    [{in: 'Record', at: 'button'}],
    'Boolean',
    false
  );
}

export function isImageElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, imageType);
}

export function isVideoEmbedElement(element: ExpressionElement): boolean {
  return isVideoElement(element) || isYouTubeVideoElement(element);
}

export function isVideoElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, videoType);
}
export function isYouTubeVideoElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, youTubeVideoType);
}

export function isDynamoWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, dynamoWrapperType);
}

export function isDynamoList(element: ExpressionElement): boolean {
  return namesEqual(element.type, dynamoListType);
}

export function isDynamoEmptyElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, dynamoEmptyType);
}

export function isPagination(element: ExpressionElement): boolean {
  return namesEqual(element.type, paginationType);
}

export function isRichText(element: ExpressionElement): boolean {
  return namesEqual(element.type, richTextType);
}

export function isFigure(element: ExpressionElement): boolean {
  return namesEqual(element.type, figureType);
}

export function isFigCaption(element: ExpressionElement): boolean {
  return namesEqual(element.type, figCaptionType);
}

export function isBgVideoWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, bgVideoWrapper);
}

export function isPlayPauseButton(element: ExpressionElement): boolean {
  return namesEqual(element.type, bgVideoPlayPauseButton);
}

// @ts-expect-error - TS7006 - Parameter 'tree' implicitly has an 'any' type.
const isFigureMediaWrapperElement_ = weakMemo((tree) =>
  // @ts-expect-error - TS7006 - Parameter 'element' implicitly has an 'any' type.
  weakMemo((element) => {
    const closestRichTextParent = Expressions.closestElement(
      tree,
      element.id,
      isRichText
    );
    const parent =
      closestRichTextParent != null && Expressions.getParent(tree, element.id);

    if (parent) {
      const isFigureBlockChild =
        namesEqual(parent.type, figureType) &&
        namesEqual(element.type, blockType);

      const isMediaFigure =
        isFigureBlockChild &&
        Expressions.getElementDataAt(
          parent,
          [
            {in: 'Record', at: 'figure'},
            {in: 'Record', at: 'type'},
          ],
          'Text',
          null
        ) === 'video';

      return isMediaFigure;
    }
    return false;
  })
);

export function isFigureMediaWrapperElement(
  element: ExpressionElement,
  tree: Expression
): boolean {
  return isFigureMediaWrapperElement_(tree)(element);
}

export function getFormType(element: ExpressionElement): string {
  return Expressions.getElementDataAt(
    element,
    [
      {in: 'Record', at: 'form'},
      {in: 'Record', at: 'type'},
    ],
    'Text',
    ''
  );
}

export function isFormFileUploadWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, formFileUploadWrapperType);
}
export function isFormFileUpload(element: ExpressionElement): boolean {
  return namesEqual(element.type, formFileUploadInputType);
}
export function isFormFileUploadErrorMsg(element: ExpressionElement): boolean {
  return namesEqual(element.type, formFileUploadErrorMsgType);
}

export function isFormCheckboxLabel(element: ExpressionElement): boolean {
  return getFormType(element) === 'checkbox-label';
}

export function isFormRadioLabel(element: ExpressionElement): boolean {
  return getFormType(element) === 'radio-label';
}

export function isSearchResultItem(element: ExpressionElement): boolean {
  return namesEqual(element.type, searchResultItemType);
}

export function isDynamoPluginElement(element: ExpressionElement): boolean {
  return (
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'dyn'},
        {in: 'Record', at: 'type'},
      ],
      'Text',
      ''
    ) !== ''
  );
}

export function isSearchPluginElement(element: ExpressionElement): boolean {
  return (
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'search'},
        {in: 'Record', at: 'type'},
      ],
      'Text',
      ''
    ) !== ''
  );
}

export function isSearchInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, searchInputType);
}

export function isSearchButton(element: ExpressionElement): boolean {
  return namesEqual(element.type, searchButtonType);
}

export function isDynamoItem(element: ElementF<unknown>): boolean {
  return namesEqual(element.type, dynamoItemType);
}

export function isLocalesWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, localesWrapperType);
}

export function isLocalesList(element: ExpressionElement): boolean {
  return namesEqual(element.type, localesListType);
}

export function isLocalesItem(element: ExpressionElement): boolean {
  return namesEqual(element.type, localesItemType);
}

export function isCommerceCartItemElement(element: ExpressionElement): boolean {
  return (
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'commerce'},
        {in: 'Record', at: 'type'},
      ],
      'Text',
      ''
    ) === 'cart-item'
  );
}

export function isUserAccountWrapper(element: ExpressionElement): boolean {
  return (
    namesEqual(element.type, legacyUserAccountWrapperType) ||
    namesEqual(element.type, userAccountWrapperType)
  );
}

export function isUserAccountSubscriptionListWrapper(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, userAccountSubscriptionListWrapperType);
}

export function isUserAccountSubscriptionList(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, userAccountSubscriptionListType);
}

export function isUserAccountSubscriptionListItem(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, userAccountSubscriptionListItemType);
}

export function isAnyUserDynListElement(element: ExpressionElement): boolean {
  return isUserAccountSubscriptionList(element);
}
export function isAnyUserDynListItemElement(
  element: ExpressionElement
): boolean {
  return isUserAccountSubscriptionListItem(element);
}

export function isCommerceAddToCartOptionListType(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceAddToCartOptionListType);
}

const commerceAddToCartOptionListWithSelectorTypesType: Qualified<Component.ComponentName> =
  qualify(
    Plugins.name('Commerce'),
    Component.name('CommerceAddToCartOptionListWithSelectorTypes')
  );

export function isCommerceAddToCartOptionListWithSelectorTypes(
  element: ExpressionElement
): boolean {
  return namesEqual(
    element.type,
    commerceAddToCartOptionListWithSelectorTypesType
  );
}

export function isAnyCommerceDynListElement(
  element: ExpressionElement
): boolean {
  return (
    namesEqual(element.type, commerceCartListType) ||
    namesEqual(element.type, commerceCheckoutOrderItemOptionListType) ||
    namesEqual(element.type, commerceCheckoutOrderItemsListType) ||
    namesEqual(element.type, commerceShippingMethodsListType) ||
    namesEqual(element.type, commerceCheckoutOrderSummaryExtraItemsListType) ||
    namesEqual(element.type, commerceAddToCartOptionListType) ||
    namesEqual(element.type, commerceCartOptionListType) ||
    namesEqual(element.type, commerceDownloadsListType)
  );
}

export function isAnyCommerceDynListItemElement(
  element: ExpressionElement
): boolean {
  return (
    isCommerceCartItemElement(element) ||
    isCommerceAddToCartOption(element) ||
    isCommerceCartOptionListItem(element) ||
    isCommerceCheckoutOrderItem(element) ||
    isCommerceShippingMethodItem(element) ||
    isCommerceCheckoutOrderSummaryExtraItemsListItem(element) ||
    isCommerceCheckoutOrderItemOptionListItem(element) ||
    isCommerceDownloadsItem(element)
  );
}

export function isAnyDynListItemElement(element: ExpressionElement): boolean {
  return (
    isDynamoItem(element) ||
    isAnyCommerceDynListItemElement(element) ||
    isAnyUserDynListItemElement(element)
  );
}

export function isProjectingElement(element: ExpressionElement): boolean {
  return (
    isDynamoItem(element) ||
    isLocalesItem(element) ||
    isSearchResultItem(element) ||
    isAnyCommerceDynListItemElement(element) ||
    isAnyUserDynListItemElement(element)
  );
}

export function isBindBoundElement(element: ExpressionElement): boolean {
  const bind = Expressions.getIn(Expressions.getElementData(element), [
    {in: 'Record', at: 'dyn'},
    {in: 'Record', at: 'bind'},
  ]);

  return bind ? Object.keys((bind as any).val).length > 0 : false;
}

export function getPropBoundFromElement(
  element: ExpressionElement,
  prop: string
): Expression | undefined {
  return Expressions.getIn(Expressions.getElementData(element), [
    {in: 'Record', at: 'dyn'},
    {in: 'Record', at: 'bind'},
    {in: 'Record', at: prop},
  ]);
}

export function isPropBoundElement(
  element: ExpressionElement,
  prop: string
): boolean {
  const boundProp = getPropBoundFromElement(element, prop);

  return boundProp ? Object.keys((boundProp as any).val).length > 0 : false;
}

/**
 * Returns all immediate references for a given element
 */
function getImmediateReferences(element: ExpressionElement) {
  return Expressions.everything(concat)(immediateReferencesEverythingMapper)(
    Expressions.getElementData(element)
  );
}

/**
 * Returns true if the provided element has *any* immediate References
 */
export function isAnyReferenceBoundElement(
  element: ExpressionElement
): boolean {
  const immediateReferences = getImmediateReferences(element);

  return immediateReferences.length > 0;
}

type ReferenceBoundType = 'Dynamo' | 'data';

/**
 * Returns an array of types that are contained on the element
 * @param element
 * @returns Set
 */
export function getReferenceBoundTypes(
  element: ExpressionElement
): Set<ReferenceBoundType> {
  const immediateReferences = getImmediateReferences(element);

  const types = new Set();

  immediateReferences.forEach((ref) => {
    const variable = Expressions.getVariable(ref);
    if (!variable) return null;
    const name = Variable.getName(variable);
    return name === 'Dynamo' || name === 'data' ? types.add(name) : null;
  });
  // @ts-expect-error - TS2322 - Type 'Set<unknown>' is not assignable to type 'Set<ReferenceBoundType>'.
  return types;
}

/**
 * Checks if the element has immediate references to 'data'.
 * If so, this means it is bound to a field
 * @param element
 * @returns {boolean}
 */
export function isElementReferenceBoundToProperty(
  element: ExpressionElement
): boolean {
  const referenceBoundTypes = getReferenceBoundTypes(element);
  if (!referenceBoundTypes) return false;
  return referenceBoundTypes.has('data');
}

/**
 * Checks if the element has immediate references to 'Dynamo'
 * If so, the element is bound to Dynamic data from the Universal Bindings list
 * @param element
 * @returns {boolean}
 */
export function isElementReferenceBoundToDynamicData(
  element: ExpressionElement
): boolean {
  const referenceBoundTypes = getReferenceBoundTypes(element);
  if (!referenceBoundTypes) return false;
  return referenceBoundTypes.has('Dynamo');
}

export function isSearchBound(element: ExpressionElement): boolean {
  const bind = Expressions.getIn(Expressions.getElementData(element), [
    {in: 'Record', at: 'search'},
    {in: 'Record', at: 'bind'},
  ]);

  return bind ? Object.keys((bind as any).val).length > 0 : false;
}

export function isDynamicConditionedElement(
  element: ExpressionElement
): boolean {
  const bind = Expressions.getIn(Expressions.getElementData(element), [
    {in: 'Record', at: 'dyn'},
    {in: 'Record', at: 'condition'},
  ]);

  return bind ? Object.keys((bind as any).val).length > 0 : false;
}

export function isDynamicBoundOrConditioned(
  element: ExpressionElement
): boolean {
  return isBindBoundElement(element) || isDynamicConditionedElement(element);
}

/**
 * Detects whether an Element is dynamic. Mostly used for UI controls to
 * determine when things should be purple.
 */

export function isDynamicElement(element: ExpressionElement): boolean {
  return (
    (isDynamoPluginElement(element) ||
      isSearchPluginElement(element) ||
      isDynamicBoundOrConditioned(element) ||
      isSearchBound(element) ||
      isAnyCommerceDynListElement(element) ||
      isAnyCommerceDynListItemElement(element) ||
      isAnyUserDynListElement(element) ||
      isAnyUserDynListItemElement(element) ||
      isElementReferenceBoundToDynamicData(element) ||
      namesEqual(element.type, commerceAddToCartOptionSelectType)) &&
    // A bit hacky - we should really move AddToCartForm bindings to atomConfig.staticBindings
    !isCommerceAddToCartForm(element)
  );
}

/**
 * Detects whether an Element is a dynamic list of any kind.
 */
export function isDynamicList(element: ExpressionElement): boolean {
  return (
    isDynamoList(element) ||
    isSearchResultList(element) ||
    isAnyCommerceDynListElement(element) ||
    isUserAccountSubscriptionList(element)
  );
}

/**
 * This checks explicitly if there is a dynamic binding on a given element, either from
 * Legacy Dynamo bindings, or the Universal Binding system.
 * @param element
 * @returns {boolean}
 */
export function isElementDynamicallyBound(element: ExpressionElement): boolean {
  return (
    isDynamicBoundOrConditioned(element) ||
    isSearchBound(element) ||
    isElementReferenceBoundToDynamicData(element)
  );
}

/**
 * Indicates whether an element belongs to a form.
 */
export function isFormElement(element: ExpressionElement): boolean {
  return (
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'form'},
        {in: 'Record', at: 'type'},
      ],
      'Text',
      ''
    ) !== ''
  );
}

export function isFormReCaptcha(element: ExpressionElement): boolean {
  return namesEqual(element.type, formReCaptchaType);
}

/**
 * Indicates whether an element is a form input that can have a form label attached to it.
 */
export function isFormInputElement(element: ExpressionElement): boolean {
  return (
    isFormSelect(element) ||
    isFormTextarea(element) ||
    isFormBasicInputElement(element) ||
    isFormFileUpload(element) ||
    isFormRadioInput(element) ||
    isFormCheckboxInput(element)
  );
}

/**
 * Indicates whether an element is an input element.
 */
export function isInput(element: ExpressionElement): boolean {
  return (
    isFormInputElement(element) ||
    isCommerceAddToCartOptionSelect(element) ||
    isCommerceAddToCartQuantityInput(element) ||
    isCommerceCartQuantity(element) ||
    isCommerceCheckoutAdditionalCheckbox(element) ||
    isCommerceCheckoutAdditionalTextArea(element) ||
    isCommerceCheckoutAdditionalTextInput(element) ||
    isCommerceCheckoutBillingAddressToggleCheckbox(element) ||
    isCommerceCheckoutBillingCity(element) ||
    isCommerceCheckoutBillingCountrySelector(element) ||
    isCommerceCheckoutBillingFullName(element) ||
    isCommerceCheckoutBillingStateProvince(element) ||
    isCommerceCheckoutBillingStreetAddress(element) ||
    isCommerceCheckoutBillingStreetAddressOptional(element) ||
    isCommerceCheckoutBillingZipPostalCode(element) ||
    isCommerceCheckoutCardNumber(element) ||
    isCommerceCheckoutDiscountsInput(element) ||
    isCommerceCheckoutEmailInput(element) ||
    isCommerceCheckoutShippingCity(element) ||
    isCommerceCheckoutShippingCountrySelector(element) ||
    isCommerceCheckoutShippingFullName(element) ||
    isCommerceCheckoutShippingStateProvince(element) ||
    isCommerceCheckoutShippingStreetAddress(element) ||
    isCommerceCheckoutShippingStreetAddressOptional(element) ||
    isCommerceCheckoutShippingZipPostalCode(element) ||
    isSearchInput(element) ||
    isUserFormEmailInput(element) ||
    isUserFormNameInput(element) ||
    isUserFormPasswordInput(element)
  );
}

/**
 * Indicates whether an element belongs to a password page form.
 */
export function isPasswordPageForm(element: ExpressionElement): boolean {
  return (
    isFormElement(element) &&
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'form'},
        {in: 'Record', at: 'passwordPage'},
      ],
      'Boolean',
      false
    )
  );
}

/**
 * Indicates whether an input element belongs to a password page form.
 */
export function isPasswordPageInput(element: ExpressionElement): boolean {
  return (
    isFormInputElement(element) &&
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'form'},
        {in: 'Record', at: 'passwordPage'},
      ],
      'Boolean',
      false
    )
  );
}

/**
 * Indicates whether a button element belongs to a password page form.
 */
export function isPasswordPageButton(element: ExpressionElement): boolean {
  return (
    isFormButtonElement(element) &&
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'form'},
        {in: 'Record', at: 'passwordPage'},
      ],
      'Boolean',
      false
    )
  );
}

/**
 * Indicates whether a element is the success / done div that shows after submit.
 */
function isFormSuccessMessageElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, formSuccessMessageType);
}

/**
 * Indicates whether a element is the error / fail div that shows if the AJAX
 * request fails.
 */
function isFormErrorMessageElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, formErrorMessageType);
}

/**
 * Indicates whether a element is a form message outside of the <form> element.
 */
export function isFormMessageElement(element: ExpressionElement): boolean {
  return (
    isFormSuccessMessageElement(element) || isFormErrorMessageElement(element)
  );
}

/**
 * Indicates whether a element is the outermost form wrapper that holds the form
 * and the success/error messages.
 */
export function isFormWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, formWrapperType);
}

/**
 * Indicates whether a element is the inner <form> directly inside the wrapper.
 */
export function isFormForm(element: ExpressionElement): boolean {
  return namesEqual(element.type, formFormType);
}

/**
 * Indicates whether a element is a form input button.
 */
export function isFormButtonElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, formButtonType);
}

/**
 * Indicates whether this is a `<input type="input">` element.
 */
export function isFormBasicInputElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, formTextInputType);
}

/**
 * Indicates whether a form element lives directly inside the <div
 * class="w-form"> wrapper.
 */
export function isFormWrapperChildElement(element: ExpressionElement): boolean {
  return isFormForm(element) || isFormMessageElement(element);
}

export function isFormInnerElement(element: ExpressionElement): boolean {
  return allPass([
    isFormElement,
    complement(isFormWrapper),
    complement(isFormForm),
  ])(element);
}

/**
 * Indicates whether an embed element is nested inside a RTE.
 */
export function isEmbedInsideRTEElement(element: ExpressionElement): boolean {
  return (
    namesEqual(element.type, htmlEmbedType) &&
    Expressions.getElementDataAt(
      element,
      [{in: 'Record', at: 'insideRTE'}],
      'Boolean',
      false
    )
  );
}

/**
 * Indicates whether this is an individual tab pane element.
 */
export function isTabsPane(element: ExpressionElement): boolean {
  return namesEqual(element.type, tabsPaneType);
}

/**
 * Indicates whether this is a tab link element.
 */
export function isTabsLinkElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, tabsLinkType);
}

/**
 * Indicates whether a element is the outer lightbox trigger wrapper element.
 */
export function isLightboxWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, lightboxWrapperType);
}

/**
 * Indicates whether a element is the brand link wrapper.
 */
export function isNavbarBrand(element: ExpressionElement): boolean {
  return namesEqual(element.type, navbarBrandType);
}

/**
 * Indicates whether this element is the wrapper element around a <file> + <label> combination.
 */
export function isFormFileUploadLabel(element: ExpressionElement): boolean {
  return namesEqual(element.type, formFileUploadLabelType);
}

/**
 * Indicates whether a element is the pagination next element.
 */
export function isPaginationNext(element: ExpressionElement): boolean {
  return namesEqual(element.type, paginationNextType);
}

/**
 * Indicates whether a element is the pagination previous element.
 */
export function isPaginationPrevious(element: ExpressionElement): boolean {
  return namesEqual(element.type, paginationPreviousType);
}

/**
 * Indicates whether a element is the pagination previous element.
 */
export function isPaginationCount(element: ExpressionElement): boolean {
  return namesEqual(element.type, paginationCountType);
}

/**
 * Indicates whether a element is a link.
 */
export function isLink(element: ExpressionElement): boolean {
  return (
    isLinkElement(element) ||
    isPaginationNext(element) ||
    isPaginationPrevious(element) ||
    isCommerceBuyNowButton(element) ||
    isCommerceCartApplePayButton(element) ||
    isCommerceCartCheckoutButton(element) ||
    isCommerceCartCloseLink(element) ||
    isCommerceCartOpenLink(element) ||
    isCommerceCartQuickCheckoutButton(element) ||
    isCommerceCartRemoveLink(element) ||
    isCommerceCheckoutPlaceOrderButton(element) ||
    isLightboxWrapper(element) ||
    isTabsLinkElement(element) ||
    isNavbarLink(element) ||
    isDropdownLink(element)
  );
}

/**
 * Checks to see if an element is a non-specialized <a> link.
 * @param  {ExpressionElement}  element
 * @return {Boolean}
 */
export function isGenericLink(element: ExpressionElement): boolean {
  return (
    isLinkElement(element) ||
    isNavbarLink(element) ||
    isDropdownLink(element) ||
    isNavbarBrand(element)
  );
}

/**
 * Indicates whether a element is allowed to store inner HTML (instead of DomNode children).
 */
export function isTextHolder(element: ExpressionElement): boolean {
  if (isLightboxWrapper(element)) {
    return false;
  }

  return isTypographic(element) || isRichText(element);
}

/**
 * Indicates whether this is a tab pane wrapper.
 */
export function isTabsContent(element: ExpressionElement): boolean {
  return namesEqual(element.type, tabsContentType);
}

/**
 * Indicates whether an element is an immediate child of the tabs wrapper.
 */
export function isTabsWrapperChild(element: ExpressionElement): boolean {
  return isTabsMenu(element) || isTabsContent(element);
}

/**
 * Indicates whether an element is part of a tabs component.
 */
export function isTabsElement(element: ExpressionElement): boolean {
  return (
    namesEqual(element.type, tabsContentType) ||
    isTabsLinkElement(element) ||
    namesEqual(element.type, tabsMenuType) ||
    namesEqual(element.type, tabsPaneType) ||
    namesEqual(element.type, tabsWrapperType)
  );
}

export function isHtmlEmbed(element: ExpressionElement): boolean {
  return namesEqual(element.type, htmlEmbedType);
}

export function isCustomCode(element: ExpressionElement): boolean {
  return namesEqual(element.type, customCodeType);
}

/**
 * Indicates whether an element is a wrapper around embedded HTML.
 */
export function isEmbed(element: ExpressionElement): boolean {
  return (
    namesEqual(element.type, htmlEmbedType) ||
    namesEqual(element.type, videoType) ||
    namesEqual(element.type, youTubeVideoType)
  );
}

/**
 * Indicates whether an element is a widget.
 */
export function isWidget(element: ExpressionElement): boolean {
  return (
    namesEqual(element.type, twitterType) ||
    namesEqual(element.type, googleType) ||
    namesEqual(element.type, facebookType) ||
    namesEqual(element.type, mapType) ||
    namesEqual(element.type, iconType) ||
    namesEqual(element.type, formFileUploadUploadingIconType)
  );
}

export function isScript(element: ExpressionElement): boolean {
  const tag = Expressions.getElementDataAt(
    element,
    [{in: 'Record', at: 'tag'}],
    'Text',
    undefined
  );
  return namesEqual(element.type, dangerousDomType) && tag === 'script';
}

/**
 * Indicates whether an element is a Facebook widget element.
 */
export function isFacebookWidget(element: ExpressionElement): boolean {
  return namesEqual(element.type, facebookType);
}

/**
 * Indicates whether an element is a Twitter widget element.
 */
export function isTwitterWidget(element: ExpressionElement): boolean {
  return namesEqual(element.type, twitterType);
}
/**
 * Indicates whether an element is a Map widget element.
 */
export function isMapWidget(element: ExpressionElement): boolean {
  return namesEqual(element.type, mapWidgetType);
}

export function isIconWidget(element: ExpressionElement): boolean {
  return namesEqual(element.type, iconType);
}

/**
 * Indicates whether an element is the outer navbar wrapper.
 */
export function isNavbarWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, navbarWrapperType);
}

/**
 * Indicates whether an element is a child of the navbar wrapper.
 */
export function isNavbarWrapperChild(element: ExpressionElement): boolean {
  return isNavbarMenu(element) || isNavbarBrand(element);
}

/**
 * Indicates whether an element is the outer dropdown wrapper.
 */
export function isDropdownWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, dropdownWrapperType);
}

/**
 * Indicates whether an element is the inner dropdown list.
 */
export function isDropdownList(element: ExpressionElement): boolean {
  return namesEqual(element.type, dropdownListType);
}

/**
 * Indicates whether an element is the outer dropdown toggle.
 */
export function isDropdownToggle(element: ExpressionElement): boolean {
  return namesEqual(element.type, dropdownToggleType);
}

/**
 * Indicates whether an element is a direct child of dropdown wrapper.
 */
export function isDropdownWrapperChild(element: ExpressionElement): boolean {
  return isDropdownToggle(element) || isDropdownList(element);
}

/**
 * Indicates whether an element is the outer tabs wrapper.
 */
export function isTabsWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, tabsWrapperType);
}

/**
 * Indicates whether this is a slider wrapper element.
 */
export function isSliderWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, sliderWrapperType);
}

/**
 * Detects the reserved slider mask element that wraps all slides.
 */
export function isSliderMask(element: ExpressionElement): boolean {
  return namesEqual(element.type, sliderMaskType);
}

/**
 * Detects whether this is a slider left/right arrow.
 */
export function isSliderArrow(element: ExpressionElement): boolean {
  return namesEqual(element.type, sliderArrowType);
}

/**
 * Indicates whether this is the slider nav icon holder.
 */
export function isSliderNav(element: ExpressionElement): boolean {
  return namesEqual(element.type, sliderNavType);
}

/**
 * Detects any immediate slider wrapper child, each of which is reserved and can't take children.
 */
export function isSliderWrapperChild(element: ExpressionElement): boolean {
  return (
    isSliderMask(element) || isSliderArrow(element) || isSliderNav(element)
  );
}

/**
 * Indicates whether this is an element from the Slider plugin.
 */
export function isSliderElement(element: ExpressionElement): boolean {
  return (
    isSliderWrapper(element) ||
    isSliderMask(element) ||
    isSliderArrow(element) ||
    isSliderNav(element) ||
    isSliderSlide(element)
  );
}

/**
 * Indicates whether this is a Form Textarea
 */
export function isFormTextarea(element: ExpressionElement): boolean {
  return namesEqual(element.type, formTextareaType);
}

/**
 * Indicates whether a DOM node is a special type of wrapper around other nodes.
 */
export function isPredefinedComponent(element: ExpressionElement): boolean {
  return (
    isEmbed(element) ||
    isWidget(element) ||
    isSliderClosedEl(element) ||
    isNavbarWrapper(element) ||
    isDropdownWrapper(element) ||
    isTabsWrapper(element)
  );
}

/**
 * Indicates whether this is a slider element that can't take direct children.
 */
function isSliderClosedEl(element: ExpressionElement): boolean {
  return isSliderWrapper(element) || isSliderWrapperChild(element);
}

/**
 * Indicates whether this is a tabs link holder.
 */
export function isTabsMenu(element: ExpressionElement): boolean {
  return namesEqual(element.type, tabsMenuType);
}

/**
 * Detects an individual slide element, which can take children.
 */
export function isSliderSlide(element: ExpressionElement): boolean {
  return namesEqual(element.type, sliderSlideType);
}
/**
 * Indicates whether this is a Form Checkbox Wrapper
 */
export function isFormCheckboxWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, formCheckboxWrapperType);
}

/**
 * Indicates whether this is a Form Radio Button Wrapper
 */
export function isFormRadioWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, formRadioWrapperType);
}

/**
 * Indicates whether this is a Form Radio Input Element
 */
export function isFormRadioInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, formRadioInputType);
}

export function isFormCheckboxInput(element: ExpressionElement): boolean {
  return namesEqual(element.type, formCheckboxInputType);
}

export function isFormCustomCheckboxInput(element: ExpressionElement): boolean {
  if (!isFormCheckboxInput(element)) return false;
  const data = Expressions.getElementData(element);
  const inputTypeExpr = Expressions.getIn(data, [
    {in: 'Record', at: 'inputType'},
  ]);
  if (!inputTypeExpr) return false;
  return Expressions.getEnum(inputTypeExpr) === 'custom';
}

export function isFormCustomRadioInput(element: ExpressionElement): boolean {
  if (!isFormRadioInput(element)) return false;
  const data = Expressions.getElementData(element);
  const inputTypeExpr = Expressions.getIn(data, [
    {in: 'Record', at: 'inputType'},
  ]);
  if (!inputTypeExpr) return false;
  return Expressions.getEnum(inputTypeExpr) === 'custom';
}

/**
 * Detects whether the form input wrapper is a Checkbox or Radio button
 */
export function isFormInputWrapper(element: ExpressionElement): boolean {
  return isFormRadioWrapper(element) || isFormCheckboxWrapper(element);
}

/**
 * Indicates whether this is a Form Select
 */
export function isFormSelect(element: ExpressionElement): boolean {
  return namesEqual(element.type, formSelectType);
}

/**
 * Indicates whether a nav item link inside the menu.
 */
export function isNavbarLink(element: ExpressionElement): boolean {
  return namesEqual(element.type, navbarLinkType);
}

export function isNavbarButton(element: ExpressionElement): boolean {
  return namesEqual(element.type, navbarButtonType);
}

export function isNavbarContainer(element: ExpressionElement): boolean {
  return namesEqual(element.type, navbarContainerType);
}

/**
 * Check to see if an element is part of an ordered or unordered list.
 */
export function isListElement(element: ExpressionElement): boolean {
  return (
    Expressions.getElementDataAt(
      element,
      [
        {in: 'Record', at: 'list'},
        {in: 'Record', at: 'type'},
      ],
      'Text',
      ''
    ) !== ''
  );
}

/**
 * Indicates whether a node is the navbar link holder.
 */
export function isNavbarMenu(element: ExpressionElement): boolean {
  return namesEqual(element.type, navbarMenuType);
}

export function isSearchForm(element: ExpressionElement): boolean {
  return namesEqual(element.type, searchFormType);
}

export function isSearchResultWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, searchResultWrapperType);
}

export function isSearchResultList(element: ExpressionElement): boolean {
  return namesEqual(element.type, searchResultListType);
}

export function isSearchResultEmptyMessage(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, searchResultEmptyType);
}

export function isCommerceAddToCartForm(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceAddToCartFormType);
}

export function isCommerceDownloadsItem(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceDownloadsItemType);
}

export function isCommerceDownloadsWrapper(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceDownloadsWrapperType);
}

export function isCommerceOrderConfirmationContainer(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceOrderConfirmationContainerType);
}

export function isCommerceCheckoutFormContainer(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCheckoutFormContainerType);
}

export function isCommerceCartWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartWrapperType);
}

export function isCommerceOrderWrapperElement(
  element: ExpressionElement
): boolean {
  return (
    isCommerceCartWrapper(element) ||
    isCommerceCheckoutFormContainer(element) ||
    isCommercePaypalCheckoutFormContainer(element) ||
    isCommerceOrderConfirmationContainer(element)
  );
}

export function isCommerceCartOpenLinkCount(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartOpenLinkCountType);
}

export function isCommerceCartItemImage(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartItemImageType);
}

export function isCommerceCartProductName(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartProductNameType);
}

export function isCommerceCartProductPrice(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceCartProductPriceType);
}

export function isCommerceCartOrderValue(element: ExpressionElement): boolean {
  return namesEqual(element.type, commerceCartOrderValueType);
}

export function isAnyTypeOfForm(element: ExpressionElement): boolean {
  return anyPass([
    isFormWrapper,
    isFormForm,
    isSearchForm,
    isCommerceAddToCartForm,
  ])(element);
}

export function isDropdownLink(element: ExpressionElement): boolean {
  return namesEqual(element.type, dropdownLinkType);
}

const blockPath: ExpressionPath = [{in: 'Record', at: 'block'}];
export function isLinkBlock(element: ExpressionElement): boolean {
  return (
    namesEqual(element.type, linkType) &&
    Expressions.getElementDataAt(element, blockPath, 'Text', '') === 'inline'
  );
}
export function isTextLink(element: ExpressionElement): boolean {
  return (
    namesEqual(element.type, linkType) &&
    Expressions.getElementDataAt(element, blockPath, 'Text', '') === ''
  );
}

/**
 * Determines if a node is explicitly marked as block.
 */
export function isBlockExplicit(element: ExpressionElement): boolean {
  const blockValue = Expressions.getIn(
    Expressions.getElementData(element),
    blockPath
  );
  if (!blockValue) return false;
  const isInlineBlock = Expressions.getText(blockValue) === 'inline';
  const isBlockForced = Expressions.getBoolean(blockValue) === true;
  return isInlineBlock || isBlockForced;
}

export function isListItem(element: ExpressionElement): boolean {
  return namesEqual(element.type, listItemType);
}
export function isList(element: ExpressionElement): boolean {
  return namesEqual(element.type, listType);
}

export function isFormInlineLabel(element: ExpressionElement): boolean {
  return namesEqual(element.type, formInlineLabelType);
}

export function isFormBlockLabel(element: ExpressionElement): boolean {
  return namesEqual(element.type, formBlockLabelType);
}

export function isFormLabel(element: ExpressionElement): boolean {
  return isFormBlockLabel(element) || isFormInlineLabel(element);
}
/**
 * Determines if an element should exist once and only once on page.
 */
export function isRequiredOnlyOnceInSite(element: ExpressionElement): boolean {
  return (
    isPasswordPageForm(element) ||
    isSearchResultWrapper(element) ||
    isSearchResultList(element) ||
    isSearchResultEmptyMessage(element)
  );
}

/**
 * Determines if an element should exist once and only once within its parent.
 */
export function isRequiredOnlyOnceInElement(
  element: ExpressionElement
): boolean {
  return isSearchInput(element) || isSearchButton(element);
}

/*
 * An EverythingMapper function to find and return an immediate Reference if it
 * exists. If we reach another Element in the Expression tree first it means
 * there isn't an immediate Reference within the provided Expression.
 */
function immediateReferencesEverythingMapper(expr: Expression) {
  if (Expressions.isVariable(expr)) {
    return Expressions.stop([expr]);
  }

  if (Expressions.isElement(expr)) {
    return Expressions.stop(emptyArray);
  }

  return Expressions.resume(emptyArray);
}

export function isAllowedTypographic(element: ExpressionElement): boolean {
  return (
    isListItem(element) ||
    isBlockquote(element) ||
    (isDivBlock(element) &&
      Expressions.getElementDataAt(
        element,
        [{in: 'Record', at: 'tag'}],
        'Text',
        Expressions.getElementDataAt(
          element,
          [{in: 'Record', at: 'tag'}],
          'Enum',
          ''
        )
      ) === 'div') ||
    // Want the following cart elements to behave as typographic
    isCommerceCartProductName(element) ||
    isCommerceCartProductPrice(element) ||
    isCommerceCartOrderValue(element) ||
    isCommerceCartOptionListItemLabel(element) ||
    isCommerceCartOptionListItemValue(element) ||
    isCommerceCheckoutOrderItemOptionListItemLabel(element) ||
    isCommerceCheckoutOrderItemOptionListItemValue(element) ||
    namesEqual(element.type, commerceCheckoutSummaryTotalType) ||
    namesEqual(element.type, commerceCheckoutSummaryTextSpacingOnDivType) ||
    namesEqual(element.type, commerceBoldTextBlockType)
  );
}

export function isCommercePaypalCheckoutFormContainer(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commercePaypalCheckoutFormContainerType);
}

export function isCommercePayPalCheckoutButton(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commercePayPalCheckoutButtonType);
}

export function isCommerceAddToCartWrapper(
  element: ExpressionElement
): boolean {
  return namesEqual(element.type, commerceAddToCartWrapperType);
}

export function isLegacyFreeModeIgnoringConstraints(
  element: ExpressionElement
): boolean {
  const dataFree = Expressions.getElementDataAt(
    element,
    [{in: 'Record', at: 'free'}],
    'Number',
    0
  );
  return dataFree === 1;
}

/**
 * Indicates whether an element belongs to a tabs link/pane pair.
 */
export const isTabsPair = (element: ExpressionElement): boolean => {
  return isTabsLinkElement(element) || isTabsPane(element);
};

/**
 * Checks whether the provided element is a Lottie element
 */
export const isLottie = (element: ExpressionElement): boolean => {
  return namesEqual(element.type, lottieType);
};

const LOTTIE_PATH_EXPRESSION_PATH = [
  {in: 'Record', at: 'animation'},
  {in: 'Union', tag: 'lottie'},
  {in: 'Record', at: 'path'},
] satisfies ExpressionPath;

/**
 * Returns the Lottie animation src for the given element, or undefined
 * if the element does not have a Lottie animation path configured.
 */
export const getLottieSrc = (
  element: ExpressionElement
): string | undefined => {
  const pathExpr = Expressions.getIn(
    Expressions.getElementData(element),
    LOTTIE_PATH_EXPRESSION_PATH
  );
  if (!pathExpr) return undefined;
  const literal = Expressions.getLiteral(pathExpr);
  const obj = getObject(literal?.value ?? null);
  const src = obj?.src;
  return src != null ? getString(src) : undefined;
};

/**
 * Checks whether a Lottie element has an animation src configured.
 */
export const hasLottieSrc = (element: ExpressionElement): boolean => {
  return getLottieSrc(element) != null;
};

/**
 * Checks whether the provided element is a Spline element
 */

export const isSpline = (element: ExpressionElement): boolean => {
  return namesEqual(element.type, splineType);
};

/**
 * Checks whether the provided element is a Rive element
 */
export const isRive = (element: ExpressionElement): boolean => {
  return namesEqual(element.type, riveType);
};

export const isPluginAnimation: (element: ExpressionElement) => boolean =
  anyPass([isLottie, isSpline, isRive]);

/** Returns whether the element is a Page Slot, formerly a Drop Target */
export function isComponentOnlyDropTarget(element: ExpressionElement): boolean {
  return namesEqual(element.type, componentOnlyDropTargetType);
}

/**
 * Checks if provided element is a slot element
 */
export function isSlot(element: ExpressionElement): boolean {
  return namesEqual(element.type, slotType);
}

/**
 * Returns whether the element is a form placeholder, where a campaign page
 * connects a HubSpot or Marketo form
 */
export function isFormPlaceholder(element: ExpressionElement): boolean {
  return namesEqual(element.type, formPlaceholderType);
}

const BGVIDEO_TYPE_PATH: ExpressionPath = [
  {in: 'Record', at: 'bgvideo'},
  {in: 'Record', at: 'type'},
];

export const isBackgroundVideoElement = (element: ExpressionElement) =>
  Expressions.getElementDataAt(
    element,
    BGVIDEO_TYPE_PATH,
    'Text',
    undefined
  ) !== undefined;

export function isContributorEditingEnabled(
  element: ExpressionElement
): boolean {
  const editable = Expressions.getIn(Expressions.getElementData(element), [
    {in: 'Record', at: 'editable'},
  ]);
  return Boolean(!editable || Expressions.getBoolean(editable));
}

export function isFrameElement(element: ExpressionElement): boolean {
  return namesEqual(element.type, frameType);
}
export function isFrameLayoutElement(element: ExpressionElement): boolean {
  return namesEqual(
    element.type,
    qualify(Plugins.name('Frame'), Component.name('FrameLayout'))
  );
}
